/**
 * PortErrorBoundary (T022) — route-level React error boundary that
 * catches port-stub failures and any other render-time exception inside
 * its subtree, renders a caller-supplied fallback, and surfaces the
 * offending port name (or a generic failure label) in a diagnostic
 * region (spec FR-054; research §4; data-model §"Route-level boundary
 * entity").
 *
 * **Class component — the single deliberate exception.** React 19 still
 * exposes error-boundary behaviour only through the class-component
 * lifecycle hooks (`static getDerivedStateFromError`,
 * `componentDidCatch`). There is no functional-component equivalent in
 * the stable React API; `react-error-boundary` is NOT installed in this
 * repo and project constraints bar adding a new dependency for
 * this seam. Every other component under `apps/web/src/shell/` is
 * functional; this file is the documented exception.
 *
 * **Usage — Cluster 1 authors, does not wrap.** Cluster 1 ships the
 * component but does NOT wrap any route with it. The first wrap lands in
 * Cluster 2 when `ContentReaderPort` arrives and the port-isolation
 * invariant (FR-054, `contracts/ports-provider.md` §"Port-isolation
 * invariant") first fires:
 *
 *     <PortErrorBoundary portName="content" fallback={<ContentUnavailable />}>
 *       <ContentRoute />
 *     </PortErrorBoundary>
 *
 * The Cluster 2 Playwright spec will run
 * `renderAppWithPorts(<App />, { content: createThrowingStub<ContentReaderPort>('content') })`
 * and assert that `/content` routes render the fallback plus a
 * diagnostic region mentioning `content`, while every other route stays
 * operational.
 *
 * **Discrimination.** `getDerivedStateFromError` inspects the caught
 * value and branches on `error instanceof NotImplementedError`:
 *
 *   - Match → the caught error carries its own `portName` (see
 *     `apps/web/src/shell/NotImplementedError.ts`); the
 *     diagnostic region labels the failure as a port-implementation gap
 *     and names the port from `error.portName`. We use the error's own
 *     `portName`, NOT the boundary's `portName` prop, so a route that
 *     happens to mount a boundary guarding port `A` but is actually
 *     broken by port `B`'s stub still surfaces the real culprit. The
 *     prop is display + routing hint; the error payload is truth.
 *
 *   - Non-match (any other thrown exception — a runtime bug, a
 *     `TypeError`, a non-`NotImplementedError` `Error`, or even a
 *     non-`Error` throw) → the diagnostic region labels the failure as
 *     a generic rendering error and surfaces the best available
 *     message. Non-`Error` throws collapse to `'Unknown error'` because
 *     there is no safe string to extract from an arbitrary thrown
 *     value.
 *
 * **Diagnostic-region shape.** A single `<div aria-hidden="true">` next
 * to the `fallback`, anchored by `data-testid="port-error-boundary-
 * diagnostic"` so Cluster 2's port-isolation Playwright spec can grep
 * for the port name. The region carries a hardcoded English diagnostic
 * string — a developer-facing label, never user-facing — and
 * `aria-hidden="true"` keeps assistive tech from announcing English
 * text on a Finnish-first product (FR-040). The fallback itself is
 * what the user sees and interacts with; a Finnish-localised fallback
 * at the route level (per the cluster that mounts this boundary)
 * carries any copy the user should hear. `role="status"` /
 * `role="alert"` was considered and rejected for the same reason —
 * both roles imply AT announcement, which the diagnostic string is
 * not designed to satisfy.
 *
 * **componentDidCatch — intentionally empty in Cluster 1.** React calls
 * this hook after `getDerivedStateFromError` with the caught error and
 * a React-supplied error-info payload. Cluster 1 has no observability
 * port; Cluster 2+ may log here through an observability adapter when
 * one exists. Leaving the method empty (with the `void` return) is
 * better than omitting it — the method signals "we know about this
 * hook and have decided not to use it yet" rather than relying on
 * React's default behaviour, and gives Cluster 2+ a one-file edit to
 * wire logging.
 *
 * **Self-retry.** The boundary does NOT self-retry on mount or on any
 * state change: once `caught !== 'none'`, the fallback stays rendered
 * until the user navigates away (React Router remounts the tree, which
 * gets a fresh boundary instance with `caught: { kind: 'none' }`).
 * Research §4 calls for "the member can always navigate away" as the
 * recovery path — the primary nav is not inside the boundary (the
 * boundary wraps each route's main content, not the shell), so
 * navigating away is always available. A retry button would require
 * clearing state AND forcing the subtree to re-mount, which React's
 * boundary API does not support without a `key` reset pattern; Cluster
 * 1's scope stops short of introducing that.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { NotImplementedError } from './NotImplementedError.js';

export interface PortErrorBoundaryProps {
  /**
   * Name of the port this boundary primarily guards. Used as the
   * diagnostic region's display hint when `children` have not yet
   * thrown — no effect on discrimination, which reads the caught
   * error's own `portName`. Typical values: `'content'`,
   * `'lakikaveri'`, `'settings'`.
   */
  readonly portName: string;
  /**
   * Minimal UI rendered in place of `children` once an error is
   * caught. Typically a "content unavailable" tree, not an error page.
   * The diagnostic region is rendered adjacent to this fallback, not
   * inside it, so the boundary can name the port without coupling the
   * fallback's DOM shape.
   *
   * A FUNCTION form receives the caught error, for the case where one
   * boundary must choose between different copy depending on WHY the
   * subtree failed — the certificate section distinguishes an upstream
   * outage ("try again later") from a member record that cannot produce a
   * certificate at all ("contact member services"), which a retry can
   * never clear. The plain `ReactNode` form is unchanged and is what every
   * other boundary uses.
   */
  readonly fallback: ReactNode | ((error: unknown) => ReactNode);
  readonly children: ReactNode;
}

/**
 * Discriminated state so the render branch can pick the right copy
 * without re-inspecting the error. `kind: 'none'` is the happy path;
 * `kind: 'port-failure'` carries the failing port's name; `kind:
 * 'unknown'` carries whatever message was extractable from the non-
 * `NotImplementedError` throw.
 */
type PortErrorBoundaryState =
  | { readonly kind: 'none' }
  | { readonly kind: 'port-failure'; readonly portName: string; readonly error: unknown }
  | { readonly kind: 'unknown'; readonly message: string; readonly error: unknown };

export class PortErrorBoundary extends Component<PortErrorBoundaryProps, PortErrorBoundaryState> {
  override state: PortErrorBoundaryState = { kind: 'none' };

  static getDerivedStateFromError(error: unknown): PortErrorBoundaryState {
    if (error instanceof NotImplementedError) {
      return { kind: 'port-failure', portName: error.portName, error };
    }
    if (error instanceof Error) {
      return { kind: 'unknown', message: error.message, error };
    }
    return { kind: 'unknown', message: 'Unknown error', error };
  }

  override componentDidCatch(_error: unknown, _info: ErrorInfo): void {
    // Intentionally empty in Cluster 1. Cluster 2+ may log via an
    // observability port when one exists. See file header.
  }

  override render(): ReactNode {
    const caught = this.state;
    if (caught.kind === 'none') {
      return this.props.children;
    }
    const diagnosticMessage =
      caught.kind === 'port-failure'
        ? `Port "${caught.portName}" is not implemented`
        : `Rendering failed: ${caught.message}`;
    return (
      <>
        {typeof this.props.fallback === 'function'
          ? this.props.fallback(caught.error)
          : this.props.fallback}
        {/*
         * Developer/test-only diagnostic — NEVER user-facing. `aria-hidden`
         * excludes it from the accessibility tree, but that alone does NOT hide
         * it from SIGHTED users (it leaked the raw error text on screen, e.g. a
         * tRPC "No procedure found on path …"). `display: none` makes it truly
         * invisible while keeping it in the DOM so the port-isolation Playwright
         * spec and the boundary unit tests can still read its `textContent` by
         * `data-testid`. The user sees only the localized `fallback` above.
         */}
        <div
          aria-hidden="true"
          data-testid="port-error-boundary-diagnostic"
          style={{ display: 'none' }}
        >
          {diagnosticMessage}
        </div>
      </>
    );
  }
}
